import { Sex } from '../../types/type';
import { toBackendDate } from '../../utils/utils';
import { FIELD_MESSAGES } from '../../utils/fieldMessages';
import { checkSnils } from '../../utils/validationUtils';

// Поля PUT /drivers/:id — те же имена бэкенд пишет в field ошибки 422
export const DRIVER_FIELDS = [
  'snils',
  'birthDate',
  'sex',
  'licenseSer',
  'licenseNumber',
  'licenseIssueDate',
  'licenseExpireDate',
] as const;

export type DriverField = (typeof DRIVER_FIELDS)[number];
export type DriverErrors = Partial<Record<DriverField, string>>;

// Значения полей формы; даты — как в <input type="date">, null — поле пустое
export interface IDriverDataForm {
  snils: string;
  birthDate: string | null;
  sex: Sex | '';
  licenseSer: string | null;
  licenseNumber: string | null;
  licenseIssueDate: string | null;
  licenseExpireDate: string | null;
}

// Тело PUT /drivers/:id
export interface IDriverDataPayload {
  snils: string;
  birthDate: string;
  sex: Sex;
  licenseSer: string;
  licenseNumber: string;
  licenseIssueDate: string;
  licenseExpireDate?: string;
}

export type DriverDataResult =
  | { payload: IDriverDataPayload; errors?: never }
  | { payload?: never; errors: DriverErrors };

export const snilsDigits = (value: string): string => value.replace(/\D/g, '');

// Ошибка введённого СНИЛС (только цифры); '' — ошибки нет. Длина и контрольное число — одна ошибка, как на бэкенде
export function snilsError(digits: string): string {
  if (!digits) return FIELD_MESSAGES.required;
  return checkSnils(digits).isValid ? '' : FIELD_MESSAGES.invalidSnils;
}

// Поле после проверки: значение для запроса или текст ошибки
type Parsed<T> = { ok: true; value: T } | { ok: false; error: string };

const ok = <T>(value: T): Parsed<T> => ({ ok: true, value });
const fail = (error: string): Parsed<never> => ({ ok: false, error });

function parseRequired<T>(value: T | null | ''): Parsed<T> {
  return value === null || value === '' ? fail(FIELD_MESSAGES.required) : ok(value);
}

function parseSnils(value: string): Parsed<string> {
  const digits = snilsDigits(value);
  const error = snilsError(digits);
  return error ? fail(error) : ok(digits);
}

function parseLicenseSer(value: string | null): Parsed<string> {
  const ser = value?.trim() ?? '';
  if (!ser) return fail(FIELD_MESSAGES.required);
  return ser.length === 4 ? ok(ser) : fail(FIELD_MESSAGES.invalidLicenseSer);
}

function parseLicenseNumber(value: string | null): Parsed<string> {
  const number = value?.trim() ?? '';
  if (!number) return fail(FIELD_MESSAGES.required);
  return /^\d{6}$/.test(number) ? ok(number) : fail(FIELD_MESSAGES.invalidLicenseNumber);
}

interface IParsedDriverData {
  snils: Parsed<string>;
  birthDate: Parsed<string>;
  sex: Parsed<Sex>;
  licenseSer: Parsed<string>;
  licenseNumber: Parsed<string>;
  licenseIssueDate: Parsed<string>;
}

type IValidDriverData = { [K in keyof IParsedDriverData]: Extract<IParsedDriverData[K], { ok: true }> };

function allValid(fields: IParsedDriverData): fields is IValidDriverData {
  return Object.values(fields).every((field) => field.ok);
}

function errorsOf(fields: IParsedDriverData): DriverErrors {
  const errors: DriverErrors = {};
  (Object.keys(fields) as (keyof IParsedDriverData)[]).forEach((name) => {
    const field = fields[name];
    if (!field.ok) errors[name] = field.error;
  });
  return errors;
}

// Все поля, кроме даты окончания в/у, обязательны (как на бэкенде).
// Возвращает либо готовое тело запроса, либо ошибки по полям.
export function validateDriverData(form: IDriverDataForm): DriverDataResult {
  const fields: IParsedDriverData = {
    snils: parseSnils(form.snils),
    birthDate: parseRequired(form.birthDate),
    sex: parseRequired(form.sex),
    licenseSer: parseLicenseSer(form.licenseSer),
    licenseNumber: parseLicenseNumber(form.licenseNumber),
    licenseIssueDate: parseRequired(form.licenseIssueDate),
  };
  if (!allValid(fields)) return { errors: errorsOf(fields) };

  const { licenseExpireDate } = form;
  return {
    payload: {
      snils: fields.snils.value,
      birthDate: toBackendDate(fields.birthDate.value),
      sex: fields.sex.value,
      licenseSer: fields.licenseSer.value,
      licenseNumber: fields.licenseNumber.value,
      licenseIssueDate: toBackendDate(fields.licenseIssueDate.value),
      ...(licenseExpireDate ? { licenseExpireDate: toBackendDate(licenseExpireDate) } : {}),
    },
  };
}
